import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { partnersService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const InactivePartnersTable = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company_name: '',
    tax_number: '',
    tax_office: '',
    address: '',
    city: '',
    district: '',
    can_issue_invoice: false
  });

  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: 'inactive',
        ...filters
      };

      const response = await partnersService.getAllPartners(params);

      if (response.success) {
        setPartners(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Partnerler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Partnerler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleEdit = async (partner) => {
    const response = await partnersService.getPartner(partner.id);
    if (response.success) {
      const partnerData = response.data;
      setSelectedPartner(partnerData);
      setFormData({
        name: partnerData.name || '',
        phone: partnerData.phone || '',
        email: partnerData.email || '',
        company_name: partnerData.company_name || '',
        tax_number: partnerData.tax_number || '',
        tax_office: partnerData.tax_office || '',
        address: partnerData.address || '',
        city: partnerData.city || '',
        district: partnerData.district || '',
        can_issue_invoice: partnerData.can_issue_invoice === 1
      });
      setShowEditModal(true);
    } else {
      toast.error(response.error || 'Partner bilgileri alınamadı');
    }
  };

  const handleSave = async () => {
    const response = await partnersService.updatePartner(selectedPartner.id, formData);
    if (response.success) {
      toast.success('Partner güncellendi');
      setShowEditModal(false);
      setSelectedPartner(null);
      loadPartners();
    } else {
      toast.error(response.error || 'Partner güncellenemedi');
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Bu partner\'ı tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    const response = await partnersService.deletePartner(partnerId);
    if (response.success) {
      toast.success('Partner silindi');
      loadPartners();
    } else {
      toast.error(response.error || 'Partner silinemedi');
    }
  };

  const handleToggleStatus = async (partnerId) => {
    const response = await partnersService.togglePartnerStatus(partnerId);
    if (response.success) {
      toast.success(response.message || 'Partner durumu değiştirildi');
      loadPartners();
    } else {
      toast.error(response.error || 'Partner durumu değiştirilemedi');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      rejected: <span className="badge bg-danger">Reddedildi</span>,
      suspended: <span className="badge bg-warning">Askıya Alındı</span>
    };
    return badges[status] || <span className="badge bg-secondary">{status}</span>;
  };

  return (
    <>
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={pagination.limit}
            onChange={handlePageSizeChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <form className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Ara (Ad, Telefon, Email, Şirket)..."
              value={filters.search}
              onChange={handleSearch}
            />
          </form>
        </div>
      </div>

      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Pasif partner bulunamadı</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="bg-light">
                <tr>
                  <th>S.L</th>
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Email</th>
                  <th>Şirket</th>
                  <th>Durum</th>
                  <th>Oluşturulma Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner, index) => (
                  <tr key={partner.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{partner.name}</td>
                    <td>{partner.phone}</td>
                    <td>{partner.email || '-'}</td>
                    <td>{partner.company_name || '-'}</td>
                    <td>{getStatusBadge(partner.status)}</td>
                    <td>{formatDate(partner.created_at)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(partner)}
                          title="Düzenle"
                        >
                          <Icon icon="mdi:pencil" />
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleToggleStatus(partner.id)}
                          title="Aktif Yap"
                        >
                          <Icon icon="mdi:toggle-switch" />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(partner.id)}
                          title="Sil"
                        >
                          <Icon icon="mdi:delete" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="card-footer border-top bg-base py-16 px-24">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className="text-sm text-secondary-light">
                Toplam {pagination.total} partner, Sayfa {pagination.page} / {pagination.totalPages}
              </span>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Önceki
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Sonraki
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Partner Düzenle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPartner(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Ad Soyad *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Telefon *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Şehir</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">İlçe</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Adres</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Şirket Adı</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Vergi Numarası</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.tax_number}
                      onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Vergi Dairesi</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.tax_office}
                      onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.can_issue_invoice}
                        onChange={(e) => setFormData({ ...formData, can_issue_invoice: e.target.checked })}
                        id="canIssueInvoiceInactive"
                      />
                      <label className="form-check-label" htmlFor="canIssueInvoiceInactive">
                        Fatura Kesebiliyor
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPartner(null);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InactivePartnersTable;

